import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useFormik } from 'formik'
import * as yup from 'yup'

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles } from 'src/v3/settings/settings.slice'
import AddIcon from '@mui/icons-material/Add'
import { LeftArrowIcon, RightArrowIcon } from 'src/v3/core/icons'
import {
  AccreditationType,
  accreditationDict,
  accreditationTypes,
  CapceForm,
  CeBrokerForm,
  NewJerseyForm,
  NewYorkForm,
  VirginiaForm,
  AccreditedForm,
  NotAccreditedForm,
  AnccForm,
} from 'src/v3/events/accreditations'
import {
  AccreditationV2,
  AccreditedAccreditationV2,
  CEBrokerAccreditationV2,
  Event,
  EventTypeName,
  NotAccreditedV2,
} from 'src/v3/core/types'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import ImageUpload from 'src/v3/components/upload-image'
import { commonValidationRules } from '../utilities/validation'
import {
  activeEventChanged,
  createEventTemplate,
  createEventTemplateAssociation,
  deleteEventTemplateAssociation,
  fetchAccreditations,
  fetchCategories,
  fetchEventAccreditations,
  fetchEventTemplateAssociations,
  fetchEventTemplates,
  fetchEvents,
  fetchInstructors,
  fetchLinkedServices,
  selectAccreditationsByTypeByEvent,
  selectAccreditationsStatus,
  selectCategories,
  selectCategoriesStatus,
  selectEventAccreditationsStatus,
  selectEventTemplateAssociations,
  selectEventTemplateAssociationsStatus,
  selectEventTemplates,
  selectEventTemplatesStatus,
  selectInstructorsByLSID,
  selectInstructorsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectTemplateByEventId,
} from './events.slice'
import { useEventAccreditationValidation } from './use-event-accreditation-validation'
import Loading from '../components/loading'
import { useScrollToFormikError } from '../utilities/hooks/useScrollToFormikError'
import SelectInstructors, {
  defaultValue,
} from '../components/form-fields/select-instructor'
import { EventWizardStep } from './types'

dayjs.extend(utc)

const ceBrokerStates = ['Florida', 'Kentucky', 'Tennessee'] as const
const ffLS = 'FOAMfrat'

type CEBrokerState = (typeof ceBrokerStates)[number]

export let EventForm = ({
  event,
  setStep,
}: {
  event?: Event
  setStep: React.Dispatch<React.SetStateAction<EventWizardStep>>
}): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')

  let categories = useAppSelector(selectCategories)
  let categoriesStatus = useAppSelector(selectCategoriesStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let eventAccreditations = useAppSelector(
    selectAccreditationsByTypeByEvent(event?.id || ''),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )
  let templates = useAppSelector(selectEventTemplates)
  let templatesStatus = useAppSelector(selectEventTemplatesStatus)
  let templateAssociations = useAppSelector(selectEventTemplateAssociations)
  let templateAssociationsStatus = useAppSelector(
    selectEventTemplateAssociationsStatus,
  )
  let currentEventTemplate = useAppSelector(
    selectTemplateByEventId(event?.id || ''),
  )

  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [accreditationType, setAccreditationType] = useState<string>('')
  let [accreditationModal, setAccreditationModal] = useState<boolean>(false)
  let [accreditations, setAccreditations] = useState<
    Partial<Record<AccreditationType, Partial<AccreditationV2>>>
  >({})
  let [ceBroker, setCeBroker] = useState<
    Partial<Record<CEBrokerState, CEBrokerAccreditationV2>>
  >({})
  let [instructorIDs, setInstructorIDs] = useState<string[]>(
    event?.instructorIDs || [defaultValue],
  )
  let [banner, setBanner] = useState<File | undefined>(undefined)
  let [thumbnail, setThumbnail] = useState<File | undefined>(undefined)
  let [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false)
  let [creatingTemplate, setCreatingTemplate] = useState<boolean>(false)
  let [templateError, setTemplateError] = useState<string>('')
  let [pendingTemplateChange, setPendingTemplateChange] = useState<{
    oldTemplateId?: string
    newTemplateId?: string
  }>({})

  let templateForm = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      statement1: yup.string().required(),
      statement2: yup.string().required(),
    }),
    initialValues: {
      name: '',
      statement1: '',
      statement2: '',
      linkedServiceId: '',
    },
    onSubmit: async (values) => {
      setCreatingTemplate(true)
      setTemplateError('')
      try {
        dispatch(
          createEventTemplate({
            name: values.name,
            statement1: values.statement1,
            statement2: values.statement2,
            linkedServiceID:
              values.linkedServiceId === ffLS
                ? undefined
                : values.linkedServiceId,
          }),
        )

        setTemplateModalOpen(false)
        templateForm.resetForm()
      } catch (error: any) {
        setTemplateError(error.message || 'Failed to create template')
      } finally {
        setCreatingTemplate(false)
      }
    },
  })

  let allAccreditations = [
    ...Object.entries(accreditations),
    ...Object.keys(ceBroker).map(
      (state): [string, Partial<AccreditationV2>] => [
        'ceBroker',
        ceBroker[state as CEBrokerState]!,
      ],
    ),
  ].filter((acc) => !!acc[1])

  let validateEventType = (name: EventTypeName) => (val: EventTypeName) =>
    val === 'hybrid' || val === name

  let deleteAccreditationIfExists = (id?: string) => {
    if (id) {
      setLoading(true)
      axios
        .delete(`/api/event/accreditation/${id}`)
        .catch((err) =>
          setErrorMessage(pluckError(err, 'Error deleting accreditation')),
        )
        .finally(() => setLoading(false))
    }
  }

  let form = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object({
      linkedService: yup.string().required(),
      templateId: yup.string().not(['unselected']).required(),
      name: yup.string().required(),
      description: yup.string().required(),
      hours: yup.number(),
      category: yup.string().required(),
      other: yup
        .string()
        .when('category', { is: 'Other', then: yup.string().required() }),
      attendanceTrackingMechanism: yup.string().required(),
      quizPasscode: yup.string().when('attendanceTrackingMechanism', {
        is: 'quizPasscode',
        then: yup.string().required(),
      }),
      eventType: yup.string().required(),
      locationName: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string().required(),
      }),
      address: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string().required(),
      }),
      city: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string().required(),
      }),
      state: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string().required().not(['unselected']),
      }),
      zip: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: commonValidationRules.postalCode.required(),
      }),
      latitude: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string(),
      }),
      logitude: yup.string().when('eventType', {
        is: validateEventType('inPerson'),
        then: yup.string(),
      }),
      virtualLocationName: yup.string().when('eventType', {
        is: validateEventType('virtual'),
        then: yup.string().required(),
      }),
      url: yup.string().when('eventType', {
        is: validateEventType('virtual'),
        then: yup.string().required(),
      }),
      accreditations: yup
        .array()
        .test(
          () =>
            allAccreditations.length > 0 &&
            (!accreditations.notAccredited ||
              !!(accreditations.notAccredited as NotAccreditedV2)?.courseCode),
        ),
      instructors: yup
        .array()
        .test(
          () =>
            instructorIDs.length > 0 &&
            instructorIDs.every((id) => id !== defaultValue),
        ),
    }),
    initialValues: {
      linkedService: event?.linkedServiceID || (event ? ffLS : ''),
      templateId: currentEventTemplate?.id || 'unselected',
      name: event?.name || '',
      description: event?.description || '',
      hours: event?.hours || 0,
      category: event?.categoryName || '',
      other: event?.category.kind === 'other' ? event?.category.other : '',
      attendanceTrackingMechanism:
        event?.attendanceTrackingMechanism.kind || '',
      quizPasscode:
        event?.attendanceTrackingMechanism.kind === 'quizPasscode'
          ? event?.attendanceTrackingMechanism.quizPasscode
          : '',
      eventType: event?.location.kind || '',
      locationName: event?.location.name || '',
      address:
        event?.location.kind !== 'virtual' ? event?.location.address : '',
      city: event?.location.kind !== 'virtual' ? event?.location.city : '',
      state:
        event?.location.kind !== 'virtual'
          ? event?.location.state
              ?.split(' ')
              .map((x) => x[0].toUpperCase() + x.slice(1))
              .join(' ') || ''
          : '',
      zip: event?.location.kind !== 'virtual' ? event?.location.zip : '',
      latitude:
        event?.location.kind !== 'virtual' ? event?.location.latitude : 0,
      longitude:
        event?.location.kind !== 'virtual' ? event?.location.longitude : 0,
      virtualLocationName:
        (event?.location as any)?.virtualName || event?.location.name || '',
      url: event?.location.kind !== 'inPerson' ? event?.location.url : '',
      featured: event?.featured || false,
      accreditations: [],
      instructors: [],
    },
    onSubmit: (values) => {
      setLoading(true)
      let location = [
        {
          name: values.locationName,
          address: values.address,
          state: values.state,
          city: values.city,
          zip: values.zip,
        },
        { name: values.virtualLocationName, url: values.url },
      ]
      let body = {
        linkedServiceID:
          values.linkedService === ffLS ? null : values.linkedService,
        name: values.name,
        description: values.description,
        hours: values.hours || undefined,
        category:
          values.category === 'Other'
            ? { other: values.other }
            : values.category,
        attendanceTrackingMechanism:
          values.attendanceTrackingMechanism === 'quizPasscode'
            ? { quizPasscode: values.quizPasscode }
            : values.attendanceTrackingMechanism,
        eventType: {
          [values.eventType]:
            values.eventType === 'inPerson'
              ? location[0]
              : values.eventType === 'virtual'
                ? location[1]
                : location,
        },
        featured: values.featured || false,
        instructorIDs,
      }
      let request = !event?.id
        ? axios.post('/api/event', body)
        : axios.put('/api/event', { id: event.id, ...body })
      request
        .then((response) => pluckData<Event>(response))
        .then((ev) =>
          Promise.all([
            banner
              ? axios
                  .put('/api/event/image', { id: ev.id, type: 'banner' })
                  .then((response) =>
                    axios.put(response.data, banner, {
                      headers: {
                        'Cache-Control': 'max-age=2592000',
                        'content-type': 'image/jpeg',
                      },
                    }),
                  )
              : Promise.resolve(),
            thumbnail
              ? axios
                  .put('/api/event/image', {
                    id: ev.id,
                    type: 'thumbnail',
                  })
                  .then((response) =>
                    axios.put(response.data, thumbnail, {
                      headers: {
                        'Cache-Control': 'max-age=2592000',
                        'content-type': 'image/jpeg',
                      },
                    }),
                  )
              : Promise.resolve(),
            ...allAccreditations
              .filter(
                ([aType, accreditation]) =>
                  accreditation &&
                  (!accreditation?.id || aType === 'notAccredited'),
              )
              .map(([aType, accreditation]) =>
                aType === 'notAccredited' &&
                eventAccreditations?.notAccredited?.[0]?.id
                  ? axios.put(
                      `/api/event/accreditation/${eventAccreditations?.notAccredited?.[0]?.id}`,
                      { [aType]: accreditation },
                    )
                  : axios.post('/api/event/accreditation', {
                      eventID: ev.id,
                      accreditation: { [aType]: accreditation },
                    }),
              ),
            // Handle template association changes
            (async () => {
              if (
                pendingTemplateChange.oldTemplateId !==
                pendingTemplateChange.newTemplateId
              ) {
                let promises = [
                  // Delete old association if it exists
                  ...(pendingTemplateChange.oldTemplateId
                    ? (() => {
                        let oldAssociation = templateAssociations.find(
                          (a) =>
                            a.eventID === ev.id &&
                            a.templateID ===
                              pendingTemplateChange.oldTemplateId,
                        )
                        return oldAssociation
                          ? [
                              dispatch(
                                deleteEventTemplateAssociation(
                                  oldAssociation.id,
                                ),
                              ),
                            ]
                          : []
                      })()
                    : []),
                  // Create new association if template is selected
                  ...(pendingTemplateChange.newTemplateId &&
                  pendingTemplateChange.newTemplateId !== 'unselected'
                    ? [
                        dispatch(
                          createEventTemplateAssociation({
                            eventId: ev.id,
                            templateId: pendingTemplateChange.newTemplateId,
                          }),
                        ),
                      ]
                    : []),
                ]

                return Promise.all(promises)
              }
              return Promise.resolve()
            })(),
          ]).then(() => dispatch(activeEventChanged(ev.id))),
        )
        .then(() => {
          dispatch(fetchEvents())
          dispatch(fetchAccreditations())
          dispatch(fetchEventAccreditations())
          dispatch(fetchEventTemplateAssociations())
        })
        .catch((err) =>
          setErrorMessage(pluckError(err, 'Event creation failed')),
        )
        .finally(() => setLoading(false))
    },
  })

  let modifiedLS =
    form.values.linkedService === ffLS ? undefined : form.values.linkedService

  let validateAccredited = useEventAccreditationValidation(
    form.values.linkedService,
    'accredited',
    (accreditations.accredited as AccreditedAccreditationV2 | undefined)?.id,
  )
  let validateNotAccredited = useEventAccreditationValidation(
    form.values.linkedService,
    'notAccredited',
    (accreditations.notAccredited as NotAccreditedV2 | undefined)?.id,
  )

  let instructors = useAppSelector(
    selectInstructorsByLSID(form.values.linkedService),
  )
  let instructorsStatus = useAppSelector(selectInstructorsStatus)

  useScrollToFormikError(form)

  useEffect(() => {
    if (categoriesStatus === 'uninitialized') {
      dispatch(fetchCategories())
    }
  }, [categoriesStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (event && accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (event && eventAccreditationsStatus === 'uninitialized') {
      dispatch(fetchEventAccreditations())
    }
  }, [eventAccreditationsStatus])

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchEventTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (templateAssociationsStatus === 'uninitialized') {
      dispatch(fetchEventTemplateAssociations())
    }
  }, [templateAssociationsStatus])

  useEffect(() => {
    if (instructorsStatus === 'uninitialized') {
      dispatch(fetchInstructors())
    } else if (event && instructorsStatus === 'fulfilled') {
      setInstructorIDs(event.instructorIDs)
    }
  }, [instructorsStatus, event])

  useEffect(() => {
    if (
      currentEventTemplate &&
      form.values.templateId !== currentEventTemplate.id
    ) {
      form.setFieldValue('templateId', currentEventTemplate.id)
      // Initialize pending change to track the original template
      if (!pendingTemplateChange.oldTemplateId) {
        setPendingTemplateChange({
          oldTemplateId: currentEventTemplate.id,
          newTemplateId: currentEventTemplate.id,
        })
      }
    } else if (
      !currentEventTemplate &&
      form.values.templateId !== 'unselected'
    ) {
      form.setFieldValue('templateId', 'unselected')
    }
  }, [currentEventTemplate, templateAssociationsStatus])

  useEffect(() => {
    if (
      event &&
      accreditationsStatus === 'fulfilled' &&
      eventAccreditationsStatus === 'fulfilled'
    ) {
      setAccreditations({
        ancc: eventAccreditations?.ancc?.[0],
        capce: eventAccreditations?.capce?.[0],
        newJersey: eventAccreditations?.newJersey?.[0],
        newYork: eventAccreditations?.newYork?.[0],
        virginia: eventAccreditations?.virginia?.[0],
        accredited: eventAccreditations?.accredited?.[0],
        notAccredited: eventAccreditations?.notAccredited?.[0],
      })
      setCeBroker({
        Florida: eventAccreditations?.ceBroker?.find(
          (ea) => ea.state === 'florida',
        ) as CEBrokerAccreditationV2,
        Kentucky: eventAccreditations?.ceBroker?.find(
          (ea) => ea.state === 'kentucky',
        ) as CEBrokerAccreditationV2,
        Tennessee: eventAccreditations?.ceBroker?.find(
          (ea) => ea.state === 'tennessee',
        ) as CEBrokerAccreditationV2,
      })
    }
  }, [accreditationsStatus, eventAccreditationsStatus])

  return (
    <form onSubmit={form.handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!event && (
          <FormControl fullWidth>
            <InputLabel id="ls-label">Linked Service</InputLabel>
            <Select
              sx={{ bgcolor: 'background.paperDark' }}
              value={form.values.linkedService}
              label="Linked Service"
              labelId="ls-label"
              name="linkedService"
              onChange={form.handleChange}
              error={form.touched.linkedService && !!form.errors.linkedService}
            >
              <MenuItem value="">Unselected</MenuItem>
              {isAdmin && <MenuItem value={ffLS}>FOAMfrat</MenuItem>}
              {linkedServices.map((ls) => (
                <MenuItem key={ls.id} value={ls.id}>
                  {ls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          variant="outlined"
          value={form.values.name}
          label="Name"
          name="name"
          onChange={form.handleChange}
          error={form.touched.name && !!form.errors.name}
        />
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          multiline
          minRows={3}
          variant="outlined"
          value={form.values.description}
          label="Description"
          name="description"
          onChange={form.handleChange}
          error={form.touched.description && !!form.errors.description}
        />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            variant="outlined"
            value={Number.isNaN(form.values.hours) ? '' : form.values.hours}
            label="Hours"
            name="hours"
            onChange={form.handleChange}
            error={form.touched.hours && !!form.errors.hours}
          />
          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              sx={{ bgcolor: 'background.paperDark' }}
              value={form.values.category}
              label="Category"
              labelId="category-label"
              name="category"
              onChange={form.handleChange}
              error={form.touched.category && !!form.errors.category}
            >
              <MenuItem value="">Unselected</MenuItem>
              {categories.standard.map((c) => (
                <MenuItem key={c} value={c}>
                  {c.replaceAll('|', '/')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.values.category === 'Other' && (
            <Autocomplete
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              freeSolo
              options={categories.custom}
              value={form.values.other}
              onChange={(e, value) => form.setFieldValue('other', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={form.values.other}
                  label="Other"
                  name="other"
                  onChange={form.handleChange}
                  error={form.touched.other && !!form.errors.other}
                />
              )}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="attendance-label">Attendance Tracking</InputLabel>
            <Select
              sx={{ bgcolor: 'background.paperDark' }}
              value={form.values.attendanceTrackingMechanism}
              label="Attendance Tracking"
              labelId="attendance-label"
              name="attendanceTrackingMechanism"
              onChange={form.handleChange}
              error={
                form.touched.attendanceTrackingMechanism &&
                !!form.errors.attendanceTrackingMechanism
              }
            >
              <MenuItem value="">Unselected</MenuItem>
              <MenuItem value="quizPasscode">Event Passcode</MenuItem>
              <MenuItem value="managerTracked">Manager Tracked</MenuItem>
            </Select>
          </FormControl>
          {form.values.attendanceTrackingMechanism === 'quizPasscode' && (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.quizPasscode}
              label="Event Passcode"
              name="quizPasscode"
              onChange={form.handleChange}
              error={form.touched.quizPasscode && !!form.errors.quizPasscode}
            />
          )}
        </Box>
        <Box>
          <Typography color="textSecondary">Location</Typography>
          <FormControl fullWidth>
            {/* <InputLabel>Location Type</InputLabel> */}
            <Select
              sx={{ bgcolor: 'background.paperDark' }}
              value={form.values.eventType}
              // label="Location Type"
              name="eventType"
              onChange={form.handleChange}
              error={form.touched.eventType && !!form.errors.eventType}
            >
              <MenuItem value="">Select Location Type</MenuItem>
              <MenuItem value="inPerson">In Person</MenuItem>
              <MenuItem value="virtual">Virtual</MenuItem>
              <MenuItem value="hybrid">Hybrid</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {(form.values.eventType === 'inPerson' ||
          form.values.eventType === 'hybrid') && (
          <>
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.locationName}
              label="Location Name"
              name="locationName"
              onChange={form.handleChange}
              error={form.touched.locationName && !!form.errors.locationName}
            />
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.address}
              label="Address"
              name="address"
              onChange={form.handleChange}
              error={form.touched.address && !!form.errors.address}
            />
            <Box sx={{ display: 'flex', gap: 3 }}>
              <TextField
                sx={{ bgcolor: 'background.paperDark' }}
                fullWidth
                variant="outlined"
                value={form.values.city}
                label="City"
                name="city"
                onChange={form.handleChange}
                error={form.touched.city && !!form.errors.city}
              />
              <StateDropdown
                value={form.values.state}
                onChange={form.handleChange}
                variant="outlined"
                error={form.touched.state && !!form.errors.state}
              />
              <TextField
                sx={{ bgcolor: 'background.paperDark' }}
                fullWidth
                variant="outlined"
                value={form.values.zip}
                label="ZIP Code"
                name="zip"
                onChange={form.handleChange}
                error={form.touched.zip && !!form.errors.zip}
              />
            </Box>
          </>
        )}
        {(form.values.eventType === 'virtual' ||
          form.values.eventType === 'hybrid') && (
          <>
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.virtualLocationName}
              label="Virtual Location Name"
              name="virtualLocationName"
              onChange={form.handleChange}
              error={
                form.touched.virtualLocationName &&
                !!form.errors.virtualLocationName
              }
            />
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.url}
              label="URL"
              name="url"
              onChange={form.handleChange}
              error={form.touched.url && !!form.errors.url}
            />
          </>
        )}
        <SelectInstructors
          instructorIDs={instructorIDs}
          setInstructorIDs={setInstructorIDs}
          instructors={instructors}
          apiPath="event"
          fetchInstructors={fetchInstructors}
          linkedServiceID={modifiedLS}
          setErrorMessage={setErrorMessage}
          submitted={!!form.submitCount}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 2,
          }}
        >
          <Box>
            <Typography color="textSecondary">Banner</Typography>
            <ImageUpload
              id="event-banner"
              type="banner"
              image={banner}
              setImage={setBanner}
              existingImage={event?.banner}
            />
          </Box>
          <Box>
            <Typography color="textSecondary">Thumbnail</Typography>
            <ImageUpload
              id="event-thumbnail"
              type="thumbnail"
              image={thumbnail}
              setImage={setThumbnail}
              existingImage={event?.thumbnail}
            />
          </Box>
        </Box>
        {isAdmin && form.values.linkedService === ffLS && (
          <FormControlLabel
            control={
              <Switch
                checked={form.values.featured}
                onChange={(e) =>
                  form.setFieldValue('featured', e.target.checked)
                }
                name="featured"
              />
            }
            label="Featured Event"
          />
        )}
        <FormControl
          fullWidth
          error={form.touched.templateId && !!form.errors.templateId}
        >
          <InputLabel>Template</InputLabel>
          <Select
            sx={{ bgcolor: 'background.paperDark' }}
            value={form.values.templateId}
            label="Template"
            name="templateId"
            onChange={(e) => {
              if (e.target.value === 'CREATE_NEW') {
                templateForm.setFieldValue(
                  'linkedServiceId',
                  form.values.linkedService,
                )
                setTemplateModalOpen(true)
              } else {
                let newTemplateId =
                  e.target.value === 'unselected' ? undefined : e.target.value
                setPendingTemplateChange({
                  oldTemplateId: currentEventTemplate?.id,
                  newTemplateId,
                })
                form.handleChange(e)
              }
            }}
            disabled={
              !form.values.linkedService || form.values.linkedService === ''
            }
          >
            <MenuItem value="unselected">Select a template</MenuItem>
            <MenuItem
              value="CREATE_NEW"
              sx={{ color: 'primary.main', fontWeight: 'medium' }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 16 }} />
              Create New Template
            </MenuItem>
            {templates
              .filter(
                (t) =>
                  t.linkedServiceID === form.values.linkedService ||
                  (isAdmin &&
                    !t.linkedServiceID &&
                    form.values.linkedService === ffLS) ||
                  (isAdmin && !t.linkedServiceID && !form.values.linkedService),
              )
              .map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
          </Select>
          {form.touched.templateId && form.errors.templateId && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, ml: 1.5 }}
            >
              {form.errors.templateId}
            </Typography>
          )}
        </FormControl>
        <Box>
          <Typography color="textSecondary">Accreditations</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <FormControl fullWidth>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={accreditationType}
                onChange={(e) => setAccreditationType(e.target.value)}
                error={form.submitCount > 0 && !allAccreditations.length}
              >
                <MenuItem value="">Select Accreditation Type</MenuItem>
                {form.values.linkedService === ffLS
                  ? accreditationTypes
                      .filter(
                        (aType) =>
                          !accreditations[aType] &&
                          aType !== 'accredited' &&
                          !(
                            aType === 'ceBroker' &&
                            ceBrokerStates.every((s) => !!ceBroker[s])
                          ),
                      )
                      .map((aType) => (
                        <MenuItem key={aType} value={aType}>
                          {accreditationDict[aType]}
                        </MenuItem>
                      ))
                  : accreditationTypes
                      .filter(
                        (aType) =>
                          !accreditations.accredited &&
                          !accreditations.notAccredited &&
                          (aType === 'accredited' || aType === 'notAccredited'),
                      )
                      .map((aType) => (
                        <MenuItem key={aType} value={aType}>
                          {accreditationDict[aType]}
                        </MenuItem>
                      ))}
              </Select>
              {form.submitCount > 0 && !allAccreditations.length && (
                <FormHelperText sx={{ color: 'error.main', height: 0 }}>
                  Each event must have at least one accreditation.
                </FormHelperText>
              )}
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={
                !accreditationType ||
                !!accreditations[accreditationType as AccreditationType]
              }
              onClick={() => setAccreditationModal(true)}
            >
              <AddIcon sx={{ px: -2 }} />
            </Button>
          </Box>
          {accreditationTypes
            .filter((aType) => accreditations[aType] || aType === 'ceBroker')
            .map((aType) =>
              aType === 'ceBroker' ? (
                ceBrokerStates
                  .filter((s) => ceBroker[s])
                  .map((state) => (
                    <Chip
                      key={state}
                      sx={{ mr: 0.5 }}
                      label={`CE Broker: ${state}`}
                      onDelete={() => {
                        deleteAccreditationIfExists(ceBroker[state]?.id)
                        setCeBroker({
                          ...ceBroker,
                          [state]: undefined,
                        })
                        setAccreditationType('')
                      }}
                    />
                  ))
              ) : (
                <Chip
                  key={aType}
                  sx={{ mr: 0.5 }}
                  label={accreditationDict[aType]}
                  onDelete={() => {
                    deleteAccreditationIfExists(accreditations[aType]?.id)
                    setAccreditations({
                      ...accreditations,
                      [aType]: undefined,
                    })
                    setAccreditationType('')
                  }}
                />
              ),
            )}
        </Box>
      </Box>
      <Box sx={{ mt: 5, display: 'flex', gap: 1 }}>
        <Button
          sx={{ color: 'text.primary' }}
          startIcon={<LeftArrowIcon />}
          onClick={() => {
            dispatch(activeEventChanged('unselected'))
            navigate('/events')
          }}
        >
          Back to Events
        </Button>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => {
            new Promise((resolve) => {
              setTimeout(resolve, 2000)
            }).then(() => {
              form.validateForm().then((data) => {
                if (!errorMessage && !Object.values(data).length)
                  navigate('/events')
              })
            })
          }}
        >
          Save & Exit
        </Button>
        <Button
          type="submit"
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => {
            new Promise((resolve) => {
              setTimeout(resolve, 2000)
            }).then(() => {
              form.validateForm().then((data) => {
                if (!errorMessage && !Object.values(data).length)
                  setStep(isAdmin ? 'syllabus' : 'sessions')
              })
            })
          }}
        >
          Next: {isAdmin ? 'Syllabus' : 'Sessions'}
        </Button>
      </Box>
      <AnccForm
        open={accreditationType === 'ancc' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => setAccreditations({ ...accreditations, ancc: a })}
      />
      <CapceForm
        open={accreditationType === 'capce' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => setAccreditations({ ...accreditations, capce: a })}
      />
      <CeBrokerForm
        open={accreditationType === 'ceBroker' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => {
          setCeBroker({ ...ceBroker, [a.state!]: a })
          setAccreditationType('')
        }}
        exclude={ceBrokerStates.filter((s) => ceBroker[s])}
      />
      <NewJerseyForm
        open={accreditationType === 'newJersey' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => setAccreditations({ ...accreditations, newJersey: a })}
      />
      <NewYorkForm
        open={accreditationType === 'newYork' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => setAccreditations({ ...accreditations, newYork: a })}
      />
      <VirginiaForm
        open={accreditationType === 'virginia' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) => setAccreditations({ ...accreditations, virginia: a })}
      />
      <AccreditedForm
        open={accreditationType === 'accredited' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) =>
          setAccreditations({ ...accreditations, accredited: a })
        }
        validateCourseCode={modifiedLS ? validateAccredited : undefined}
      />
      <NotAccreditedForm
        open={accreditationType === 'notAccredited' && accreditationModal}
        setOpen={setAccreditationModal}
        onSubmit={(a) =>
          setAccreditations({ ...accreditations, notAccredited: a })
        }
        validateCourseCode={modifiedLS ? validateNotAccredited : undefined}
      />
      <Dialog
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={templateForm.handleSubmit}>
          <DialogTitle>Create New Template</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              <TextField
                fullWidth
                label="Template Name"
                name="name"
                value={templateForm.values.name}
                onChange={templateForm.handleChange}
                error={templateForm.touched.name && !!templateForm.errors.name}
                helperText={
                  templateForm.touched.name && templateForm.errors.name
                }
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Accrediting Agency 1 Statement"
                name="statement1"
                value={templateForm.values.statement1}
                onChange={templateForm.handleChange}
                error={
                  templateForm.touched.statement1 &&
                  !!templateForm.errors.statement1
                }
                helperText={
                  templateForm.touched.statement1 &&
                  templateForm.errors.statement1
                }
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Accrediting Agency 2 Statement"
                name="statement2"
                value={templateForm.values.statement2}
                onChange={templateForm.handleChange}
                error={
                  templateForm.touched.statement2 &&
                  !!templateForm.errors.statement2
                }
                helperText={
                  templateForm.touched.statement2 &&
                  templateForm.errors.statement2
                }
              />
              <Typography variant="caption" color="text.secondary">
                *In an accreditation statement that includes the length of the
                course, enter "$HOUR" and the length of the course will be
                inserted in the certificate.
              </Typography>
              {templateError && <Alert severity="error">{templateError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={creatingTemplate}
            >
              {creatingTemplate ? 'Creating...' : 'Create Template'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[
          categoriesStatus,
          linkedServicesStatus,
          templatesStatus,
          templateAssociationsStatus,
        ]}
        loading={loading}
      />
    </form>
  )
}

export default EventForm
